/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable react-hooks/rules-of-hooks */
/* eslint-disable @typescript-eslint/ban-types */
/* AdvaHealth Solutions Pty. Ltd. ("AHS") CONFIDENTIAL
 * Copyright (c) 2024 AdvaHealth Solutions Pty. Ltd. All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains the property of AHS. The intellectual and technical concepts contained
 * herein are proprietary to AHS and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material is strictly forbidden unless prior written permission is obtained
 * from AHS.  Access to the source code contained herein is hereby forbidden to anyone except current AHS employees, managers or contractors who have executed
 * Confidentiality and Non-disclosure agreements explicitly covering such access.
 *
 * The copyright notice above does not evidence any actual or intended publication or disclosure  of  this source code, which includes
 * information that is confidential and/or proprietary, and is a trade secret, of AHS. ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC  PERFORMANCE,
 * OR PUBLIC DISPLAY OF OR THROUGH USE  OF THIS  SOURCE CODE  WITHOUT  THE EXPRESS WRITTEN CONSENT OF COMPANY IS STRICTLY PROHIBITED, AND IN VIOLATION OF APPLICABLE
 * LAWS AND INTERNATIONAL TREATIES.  THE RECEIPT OR POSSESSION OF  THIS SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS
 * TO REPRODUCE, DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT  MAY DESCRIBE, IN WHOLE OR IN PART.
 */

/**
 * ReportViewer
 * ============
 * Fetches `src` as a blob and renders it either as:
 *   - an image, inside an inline viewerjs instance (zoom / rotate / flip / prev / next toolbar), or
 *   - a PDF, delegated to <DocumentViewer type="PDF" /> (detected by the response content-type).
 *
 * Consumers (all pass `src` as a URL to the instance render/thumbnail API):
 *   - application-ui  pages/study/ViewerModal.tsx            src = selectedInstance?.src || ""
 *   - application-ui  pages/study/ViewInstanceModal.tsx      src set from an effect (frame / instance)
 *   - application-ui  pages/studyValidation/ViewerModal.tsx  src set from an effect (series / instance)
 *   - patient-ui      components/Viewer/ViewerModal.tsx      src = selectedInstance?.src || "", uses ref + setImageLoadingProp
 *   (patient-ui pages/authenticated/exams/ReportViewer.tsx and the worklist-ui viewers are separate components.)
 *
 * Imperative handle (ref):
 *   - handleAction(type): zoom / rotate / flip / reset when the viewer exists; NEXT / PREV always call the callbacks.
 *   - getImgBase64():     the raw blob of the last successful fetch (undefined while loading, on error, or src === "").
 *
 * State
 * -----
 *   imgBase64      object URL of the fetched image (or the cached one from sessionStorage).
 *   pdfBase64      object URL of the fetched PDF. When not "", the component renders DocumentViewer and the
 *                  <img> is NOT mounted (imageRef.current === null).
 *   unloadedBase64 data URL of the "Image unavailable" placeholder, only created when the fetch failed.
 *   imgBase64Data  raw blob returned by getImgBase64().
 *   apiLoaded      false while fetching, true once the (non-stale) fetch finished.
 *   viewer         the viewerjs instance bound to <img>. Must only be created while <img> is mounted.
 *
 * Render cases
 * ------------
 *   1. src === "" (initial)        no fetch, no viewer -> empty ViewerLoader area (hidden <img src="">).
 *   2. src -> ""  (from a value)   previous viewer is destroyed and all state cleared -> empty area
 *                                  (happens on series switch, where the instance list is briefly empty).
 *   3. instance cached             (sessionStorage "cached_instances" has instanceId) -> no request,
 *                                  show the cached object URL, clear any previous PDF.
 *   4. fetch OK, image             imgBase64 set -> viewer created (or updated) on <img>.
 *   5. fetch OK, PDF               pdfBase64 set -> DocumentViewer, viewer cleared (no <img>).
 *   6. fetch failed                state cleared -> "Image unavailable" placeholder shown in the viewer.
 *   7. src changes while fetching  the stale response is dropped (see "Effects / src" below).
 *
 * Effects
 * -------
 *   [src, showHtmlOnly, cachedImage]
 *     Loads the content for the current src (cases 1-3, 7). Each run owns an AbortController; the cleanup
 *     aborts it and resets the loading flags, so a response for a previous src (or after unmount) is ignored
 *     and cannot overwrite the current instance or turn off the loader of the new request.
 *     NOTE: the signal is intentionally NOT passed to axios. A canceled request has no `response`, and the
 *     app axios interceptors treat that as "Network Error - Unable to connect to API". The request still
 *     completes on the network, only its result is discarded.
 *   [apiLoaded]
 *     Builds the "Image unavailable" placeholder when a fetch finished with nothing to render
 *     (imgBase64 === "" && pdfBase64 === ""), i.e. the fetch failed.
 *   [isResize]
 *     Destroys and re-creates the viewer so it fits the new container size. Skipped while a PDF is shown.
 *   [unloadedBase64, imgBase64]
 *     Creates the viewer on first content, then viewer.update() on next contents. Skipped when <img> is not
 *     mounted (PDF case) because viewerjs throws on a null element.
 *   <img onLoad>
 *     Stores instanceId -> imgBase64 in sessionStorage "cached_instances" (consumers clear it on series change).
 *
 * Known limitations
 * -----------------
 *   - Object URLs (image / PDF) are never revoked.
 *   - Image -> PDF only drops the viewer reference (setViewer(undefined)) without viewer.destroy().
 *   - Cached entries are object URLs: they are only valid in the same document (not after a page reload).
 *   - When `instanceId` changes one render before `src` (ViewInstanceModal), the old src is fetched once more;
 *     that response is discarded by the abort guard.
 *
 * History of fixed issues
 * -----------------------
 *   AX-4753  Instance without pixel data: show the "Image unavailable" placeholder, use ViewerLoader.
 *   AX-5305  Refactor: render PDF from a blob object URL instead of a base64 data URL.
 *   AX-5385  Allow prev / next actions from the toolbar and handleAction even before the viewer exists.
 *   AX-5353  Viewer issues: placeholder only after the API finished (apiLoaded), remove the hidden-until-loaded
 *            wrapper that left the viewer blank.
 *   AX-5433  Cache loaded images per instanceId in sessionStorage (behind the "cached-images" flag).
 *   AX-5462  Only write the cache when an image was actually fetched (no empty entries).
 *   AX-5686  Deprecate the "cached-images" flag: cache always on.
 *   Check nullable attr   viewerjs `preload: false`: with a single image and loop false, viewerjs preloaded
 *                         images[-1] and threw in inheritAttributes.
 *   Viewer PDF case       Crash "The first argument is required and must be an element" when loading a PDF:
 *                         the placeholder was created even on success, which triggered `new Viewer(null)` while
 *                         DocumentViewer was rendered. Fixed by building the placeholder only on failure and
 *                         guarding viewer creation / resize on imageRef.current.
 *   Check string empty    src -> "" kept showing the previous image / PDF (and getImgBase64 returned the old
 *                         blob). Now the viewer and state are cleared. The cache branch also clears a previous PDF.
 *   Race condition        Fast prev / next (or series switch) could show a previous instance when its response
 *                         arrived last. Responses for a stale src are now discarded (AbortController guard).
 */
import React, {
  FC,
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import axios from "axios";
import Viewer from "viewerjs";
import "viewerjs/dist/viewer.css";
import { ContentType, ViewerButtonType } from "../../constant";
import { useDebouncedCallback } from "use-debounce";
import { DocumentViewer } from "./DocumentViewer";
import { ViewerLoader } from "../../components/Loader/ViewerLoader";
import ImageUnavailable from "../../assets/icons/Image-icon-text-M-break.svg";

export interface IReportViewer {
  ref?: any;
  src: string;
  isResize?: boolean;
  showHtmlOnly?: boolean;
  wrapperClassName?: string;
  showToolBarButton?: boolean;
  instanceId?: string;
  showPrevInstance?: () => void;
  showNextInstance?: () => void;
  setImageLoadingProp?: (loading: boolean) => void;
}

interface ToolbarButtonOptions {
  show?: boolean | Viewer.Visibility;
  size?: Viewer.ToolbarButtonSize;
  click?: Viewer.ToolbarButtonClick;
}

export const ReportViewer: FC<IReportViewer> = forwardRef(
  (
    {
      showNextInstance,
      showPrevInstance,
      src,
      isResize = false,
      showHtmlOnly = false,
      wrapperClassName = "",
      showToolBarButton = true,
      setImageLoadingProp,
      instanceId = "",
    },
    ref
  ) => {
    const imageContainerRef = useRef<HTMLDivElement>(null);
    const imageRef = useRef<HTMLImageElement>(null);
    const [apiLoaded, setApiLoaded] = useState(false);
    const [viewer, setViewer] = useState<Viewer>();
    const [imgBase64, setImgBase64] = useState("");
    const [imgBase64Data, setImgBase64Data] = useState();
    const [imgLoading, setImageLoading] = useState(false);
    const [pdfBase64, setPdfBase64] = useState("");
    const [unloadedBase64, setUnloadedBase64] = useState("");
    const [rightMouseDown, setRightMouseDown] = useState(false);
    const [leftMouseDown, setLeftMouseDown] = useState(false);
    const [previousMousePosition, setPreviousMousePosition] = useState({
      x: 0,
      y: 0,
    });
    const [flipHorizotal, setFlipHorizotal] = useState(false);
    const [flipVertical, setFlipVertical] = useState(false);

    const debouncedShowNextInstance = showNextInstance
      ? useDebouncedCallback(showNextInstance, 500)
      : null;

    const debouncedShowPrevInstance = showPrevInstance
      ? useDebouncedCallback(showPrevInstance, 500)
      : null;

    const getImgBase64 = async () => {
      return imgBase64Data;
    };
    const handleAction = (type: ViewerButtonType) => {
      if (viewer) {
        switch (type) {
          case ViewerButtonType.ZOOM_IN:
            return viewer.zoom(0.25);
          case ViewerButtonType.ZOOM_OUT:
            return viewer.zoom(-0.25);
          case ViewerButtonType.ONT_TO_ONE:
            return viewer.zoomTo(1);
          case ViewerButtonType.RESET:
            viewer.zoomTo(1);
            viewer.rotateTo(0);
            viewer.scaleX(1);
            viewer.scaleY(1);
            break;
          case ViewerButtonType.ROTATE_LEFT:
            return viewer.rotate(-90);
          case ViewerButtonType.ROTATE_RIGHT:
            return viewer.rotate(90);
          case ViewerButtonType.FLIP_HORIZONTAL:
            setFlipHorizotal(!flipHorizotal);
            return viewer.scaleX(flipHorizotal ? -1 : 1);
          case ViewerButtonType.FLIP_VERTICAL:
            setFlipVertical(!flipVertical);
            return viewer.scaleY(flipVertical ? -1 : 1);
        }
      }

      switch (type) {
        case ViewerButtonType.NEXT:
          showNextInstance?.();
          break;
        case ViewerButtonType.PREV:
          showPrevInstance?.();
          break;
      }
    };

    useImperativeHandle(ref, () => ({ handleAction, getImgBase64 }));

    /**
     * Viewer config and functions
     * @returns
     */
    const centerImage = () => {
      if (!imageContainerRef || !viewer) return;
      viewer.zoomTo(1);
      viewer.full();
      window.dispatchEvent(new Event("resize"));
    };

    const buttonOptions = (
      func: (() => void) | undefined,
      show: boolean | Viewer.Visibility
    ): ToolbarButtonOptions => {
      return {
        click: func,
        show: show,
        size: "large" as Viewer.ToolbarButtonSize,
      };
    };

    const initViewer = () => {
      // @ts-ignore
      return new Viewer(imageRef?.current, {
        backdrop: true,
        zoomOnWheel: false,
        zoomRatio: 0.25,
        inline: true,
        navbar: false,
        button: false,
        loop: false,
        // Single image: nothing to preload. With loop false, viewerjs preloads images[-1] and throws in inheritAttributes.
        preload: false,
        title: false,
        transition: false,
        toggleOnDblclick: false,
        viewed: () => {
          centerImage();
        },
        toolbar: {
          zoomIn: buttonOptions(undefined, showToolBarButton),
          zoomOut: buttonOptions(undefined, showToolBarButton),
          oneToOne: buttonOptions(undefined, showToolBarButton),
          reset: buttonOptions(undefined, showToolBarButton),
          prev: buttonOptions(showPrevInstance, showToolBarButton),
          next: buttonOptions(showNextInstance, showToolBarButton),
          rotateLeft: buttonOptions(undefined, showToolBarButton),
          rotateRight: buttonOptions(undefined, showToolBarButton),
          flipHorizontal: buttonOptions(undefined, showToolBarButton),
          flipVertical: buttonOptions(undefined, showToolBarButton),
        },
      });
    };

    const createNewInstanceViewer = () => {
      const instance = initViewer();
      instance.update();
      setViewer(instance);
    };

    const scroll = (event: React.WheelEvent<HTMLDivElement>) => {
      if (!debouncedShowNextInstance && !debouncedShowNextInstance) return;
      const { deltaY } = event;
      if (deltaY > 0) window.requestAnimationFrame(debouncedShowNextInstance);
      else if (deltaY < 0)
        // @ts-ignore
        window.requestAnimationFrame(debouncedShowPrevInstance);
    };

    const clearMouseState = () => {
      setPreviousMousePosition({ x: 0, y: 0 });
      setRightMouseDown(false);
      setLeftMouseDown(false);
    };

    const onMouseMove = (event: React.MouseEvent<HTMLDivElement>) => {
      if (!viewer) return;
      if (rightMouseDown) {
        const delta = {
          x: previousMousePosition.x - event.pageX,
          y: previousMousePosition.y - event.pageY,
        };

        if (Math.abs(delta.y) > Math.abs(delta.x) && delta.y > 0) {
          viewer.zoom(0.025 * Math.abs(delta.y));
        } else if (Math.abs(delta.y) > Math.abs(delta.x) && delta.y < 0) {
          viewer.zoom(-0.025 * Math.abs(delta.y));
        }
        setPreviousMousePosition({
          x: event.pageX,
          y: event.pageY,
        });
      } else if (leftMouseDown) {
        viewer.rotate(90);
      }
    };

    const onMouseDown = (event: React.MouseEvent<HTMLDivElement>) => {
      if (event.nativeEvent.which === 3) {
        setRightMouseDown(true);
        setPreviousMousePosition({ x: event.pageX, y: event.pageY });
      }
    };

    /**
     * Main functions
     */

    const clearState = () => {
      // setViewer(undefined);
      // Clear base64 for image
      setImgBase64("");

      // Clear base64 for pdf
      setPdfBase64("");
    };

    const clearImage = () => {
      setImgBase64("");
      setViewer(undefined);
    };

    const clearPdf = () => {
      setPdfBase64("");
    };

    const fetchDataFromSrc = async (url: string, signal: AbortSignal) => {
      try {
        setApiLoaded(false);
        setImageLoading(true);
        if (setImageLoadingProp) setImageLoadingProp(true);
        setImgBase64Data(undefined);
        // signal is not passed to axios: a canceled request has no response and the
        // app interceptors would show it as a "Network Error".
        const rs = await axios.get(url, {
          responseType: "blob",
        });
        // src changed while fetching: drop the stale response.
        if (signal.aborted) return;
        setImgBase64Data(rs.data);

        // handle pdf, text or image.
        if (rs.headers["content-type"] === ContentType.APPLICATION_PDF) {
          // is pdf file.
          clearImage();

          const blobUrl = URL.createObjectURL(rs.data);
          setPdfBase64(blobUrl);
        } else {
          clearPdf();

          const result = window.URL.createObjectURL(new Blob([rs.data]));
          setImgBase64(result);
        }
        setUnloadedBase64("");
        setImageLoading(false);
      } catch (error) {
        if (signal.aborted) return;
        clearState(); // Reset state
      } finally {
        // aborted: loading is reset in the effect cleanup, the next fetch owns the state.
        if (!signal.aborted) {
          setImageLoading(false);
          if (setImageLoadingProp) setImageLoadingProp(false);
          setApiLoaded(true);
        }
      }
    };

    // allow cached images with session storage and ff.
    const cachedImage = useMemo(() => {
      const dataStored = sessionStorage.getItem("cached_instances")
        ? // @ts-ignore
          JSON.parse(sessionStorage.getItem("cached_instances"))
        : [];
      const data = new Map(dataStored);

      const dataCached = data.has(instanceId);
      return {
        shouldCached: dataCached,
        imageCached: data.get(instanceId) as string,
      };
    }, [instanceId]);

    /** Effects
     *
     */
    useEffect(() => {
      const controller = new AbortController();
      if (src !== "") {
        if (!cachedImage.shouldCached)
          fetchDataFromSrc(src, controller.signal); // Fetch data from url
        else {
          clearPdf();
          setImgBase64(cachedImage.imageCached);
        }
      } else {
        // no src: don't keep showing the previous instance.
        viewer?.destroy();
        clearImage();
        clearPdf();
        setUnloadedBase64("");
        setImgBase64Data(undefined);
      }
      return () => {
        // cancel the in-flight request of the previous src (or on unmount).
        controller.abort();
        setImageLoading(false);
        if (setImageLoadingProp) setImageLoadingProp(false);
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [src, showHtmlOnly, cachedImage]);

    // no pixel to blob
    useEffect(() => {
      const convertToBase64 = async () => {
        const response = await fetch(ImageUnavailable);
        const blob = await response.blob();

        const reader = new FileReader();
        reader.readAsDataURL(blob);
        reader.onloadend = () => {
          // @ts-ignore
          setUnloadedBase64(reader.result);
        };
      };

      // only show the unavailable image when the fetch failed (nothing to render).
      if (apiLoaded && imgBase64 === "" && pdfBase64 === "") convertToBase64();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [apiLoaded]);

    useEffect(() => {
      // pdf is rendered by DocumentViewer, <img> is not mounted.
      if (isResize && imageRef.current) {
        viewer?.destroy(); // destroy and re-init viewer
        createNewInstanceViewer();
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isResize]);

    useEffect(() => {
      // pdf is rendered by DocumentViewer, <img> is not mounted.
      if (!imageRef.current) return;
      if (unloadedBase64 !== "" || imgBase64 !== "") {
        if (!viewer) {
          const instance = initViewer();

          instance.update();
          setViewer(instance);
        } else {
          viewer.update();
        }
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [unloadedBase64, imgBase64]);

    /**
     * Memo variables check
     */
    if (pdfBase64 !== "") {
      return <DocumentViewer type="PDF" src={pdfBase64} />;
    }

    return (
      <ViewerLoader loading={imgLoading} className="border-card-light">
        <div
          className={`view-documents-image-full-view w-100 ${wrapperClassName}`}
          ref={imageContainerRef}
          onContextMenu={(e) => e.preventDefault()}
          onMouseMove={onMouseMove}
          onMouseUp={clearMouseState}
          onBlur={clearMouseState}
          onMouseDown={onMouseDown}
          onWheel={scroll}
          role="presentation"
        >
          <img
            id={instanceId}
            style={{ opacity: 0 }}
            ref={imageRef}
            src={imgBase64 || unloadedBase64}
            alt={instanceId}
            onLoad={() => {
              const dataStored = sessionStorage.getItem("cached_instances")
                ? // @ts-ignore
                  JSON.parse(sessionStorage.getItem("cached_instances"))
                : [];
              const data = new Map(dataStored);

              if (imgBase64) {
                // always cached when imaging is loading.
                data.set(instanceId, imgBase64);

                // Convert Map to array, then JSON string
                const mapArray = Array.from(data.entries());
                sessionStorage.setItem(
                  "cached_instances",
                  JSON.stringify(mapArray)
                );
              }
            }}
          />
        </div>
      </ViewerLoader>
    );
  }
);
